import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  RegisterUserBody,
  RegisterUserData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface RegisterUserState
  extends MergeRequestState<RegisterUserData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.registerUser,
  isFetching: (state: ReduxState) => state.server.registerUser.isFetching,
  data: (state: ReduxState) => state.server.registerUser.data,
  error: (state: ReduxState) => state.server.registerUser.error,
};

const initialState: RegisterUserState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/registerUser';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: RegisterUserBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.registerUser(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerRegisterUser {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const registerUser: ServerRegisterUser = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
